export enum SegmentType {
	Unknown = 0,
	Text = 1, // plain text
	Url = 2, // link
	Image = 3, // inline image
	Email = 4, // email, subset of URL
}
export enum MentionType {
	Unknown = 0,
	User = 1, // user
	DataSheet = 2, // datasheet
	Group = 3, // group
}
export type IBaseSegment = {
	text: string
}

export type ITextSegment = {
	type: SegmentType.Text
} & IBaseSegment
export type IHyperlinkSegment = {
	type: SegmentType.Url
	link: string // In the case of pure link, link is the same as text
	title?: string
	favicon?: string
	visited?: boolean
} & IBaseSegment

export type IEmailSegment = {
	type: SegmentType.Email
	link: string // In the case of pure link, link is the same as text
} & IBaseSegment

export type ISegment = ITextSegment | IHyperlinkSegment | IEmailSegment

export type InternalState = {
	unitId: string
}

export type IBaseField = {
	id: string
	name: string
	desc?: string
	required?: boolean
}
export enum FieldType {
	Text = 'text',
	NotSupport = 'not_support',
	// Number fields
	Number = 'number',
	Delta = 'delta',
	Currency = 'currency',
	Price = 'price',
	Percent = 'percent',
	// Boolean fields
	Checkbox = 'checkbox',
	Flag = 'flag',
	Lock = 'lock',
	// Select fields
	SingleSelect = 'single_select',
	MultiSelect = 'multi_select',

	/* Tree = 'tree',
	Url = 'url',
	Image = 'image',
	Choice = 'choice',
	Badge = 'badge',
	Chip = 'chip',
	Count = 'count',
	DateDistanceAt = 'date_distance_at',
	Date = 'date',
	Datetime = 'datetime',
	Custom = 'custom', */
}
export type INumberBaseFieldPropertyFormat = {
	formatType: number
	precision: number
	symbol?: string
}
/** Calculated field: formula & lookup When the return value is number or datetime, additional formatting conditions can be set. */
export type IComputedFieldFormattingProperty = INumberBaseFieldPropertyFormat

export type INumberFieldProperty = {
	precision: number
	defaultValue?: string
	commaStyle?: string
	symbol?: string
}

export type INotSupportFieldProperty = any
export type ITextFieldProperty = null
export type ICheckboxFieldProperty = null
export type ICurrencyFieldProperty = {
	symbol: string
	precision: number
	defaultValue?: string
}

export type ITextField = {
	type: FieldType.Text
	property: ITextFieldProperty
} & IBaseField

export type INumberField = {
	type: FieldType.Number
	property: INumberFieldProperty
} & IBaseField

export type INotSupportField = {
	type: FieldType.NotSupport
	property: INotSupportFieldProperty
} & IBaseField

export type ICurrencyField = {
	type: FieldType.Currency
	property: ICurrencyFieldProperty
} & IBaseField

export type IPercentFieldProperty = {
	precision: number
	defaultValue?: string
}

export type IPercentField = {
	type: FieldType.Percent
	property: IPercentFieldProperty
} & IBaseField

export type ICheckboxField = {
	type: FieldType.Checkbox
	property: ICheckboxFieldProperty
} & IBaseField

export type IField =
	| ITextField
	| INumberField
	| INotSupportField
	| ICurrencyField
	| IPercentField
	| ICheckboxField

export type ICellValue = string | number | boolean | null
export type BasicOpenValueTypeBase = string | number | boolean

export type BasicOpenValueType = BasicOpenValueTypeBase

export type IFieldProperty =
	| ITextFieldProperty
	| INumberFieldProperty
	| INotSupportFieldProperty
	| ICheckboxFieldProperty

export enum BasicValueType {
	String = 'string',
	Number = 'number',
	DateTime = 'date_time',
	Array = 'array',
	Boolean = 'boolean',
}

export enum FOperator {
	Is = 'is',
	IsNot = 'is_not',
	Contains = 'contains',
	NotContains = 'not_contains',
	In = 'in',
	NotIn = 'not_in',
	IsEmpty = 'is_empty',
	IsNotEmpty = 'is_not_empty',
	IsGreater = 'gt',
	IsGreaterEqual = 'gte',
	IsLess = 'lt',
	IsLessEqual = 'lte',
	Equal = 'eq',
	NotEqual = 'ne',
	NumberBetween = 'num_between',
	Boolean = 'bl',
	ArrayAnyOf = 'array_any_of',
	ArrayAllOf = 'array_all_of',
	ArrayExact = 'array_exact',
	ArrayNoneOf = 'array_none_of',
	DateBetween = 'date_between',
	DateAfter = 'date_after',
	DateBefore = 'date_before',
	DateAt = 'date_at',
	DescendantOf = 'descendant_of',
}

export const FOperatorDescMap: Record<FOperator, string> = {
	[FOperator.Is]: 'equal',
	[FOperator.IsNot]: 'not_equal',
	[FOperator.Contains]: 'contains',
	[FOperator.NotContains]: 'not_contains',
	[FOperator.In]: 'in',
	[FOperator.NotIn]: 'not_in',
	[FOperator.IsEmpty]: 'is_empty',
	[FOperator.IsNotEmpty]: 'is_not_empty',
	[FOperator.IsGreater]: 'gt',
	[FOperator.IsGreaterEqual]: 'gte',
	[FOperator.IsLess]: 'lt',
	[FOperator.IsLessEqual]: 'lte',
	[FOperator.Equal]: 'eq',
	[FOperator.NotEqual]: 'ne',
	[FOperator.NumberBetween]: 'num_between',
	[FOperator.Boolean]: 'bl',
	[FOperator.ArrayAnyOf]: 'array_any_of',
	[FOperator.ArrayAllOf]: 'array_all_of',
	[FOperator.ArrayExact]: 'array_exact',
	[FOperator.ArrayNoneOf]: 'array_none_of',
	[FOperator.DateBetween]: 'date_between',
	[FOperator.DateAfter]: 'date_after',
	[FOperator.DateBefore]: 'date_before',
	[FOperator.DateAt]: 'date_at',
	[FOperator.DescendantOf]: 'descendant_of',
}

export type IOpenFilterValueString = string | null
export type IOpenFilterValueNumber = number | null
export type IOpenFilterValueBoolean = boolean | null
export type IOpenFilterValueArray = string[] | null
export type IOpenFilterValueSelect = string | null
export type IOpenFilterValueMultiSelect = string[] | null

export type IOpenFilterValue =
	| IOpenFilterValueString
	| IOpenFilterValueNumber
	| IOpenFilterValueBoolean
	| IOpenFilterValueArray
	| IOpenFilterValueSelect
	| IOpenFilterValueMultiSelect
	| IOpenFilterValueString[]
	| IOpenFilterValueNumber[]
	| IOpenFilterValueBoolean[]
	| IOpenFilterValueSelect[]
	| IOpenFilterValueMultiSelect[]

export type OpenFOperator = FOperator
